import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useRef,
} from "react";
import { Input, Modal, Tooltip } from "antd";
// Components
import InputWithLabel, {
  formatConstraintValue,
} from "components/InputWithLabel";
// Style
import "./AdditionalData.scss";
import { fetchAdditionalData } from "api/auditTrailApi";
import { GlobalContext } from "contexts/GlobalContext";
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";
import PreLoadPagesSpin from "components/PreLoadPagesSpin";
import { updateAdditionalInfoField } from "api/pricerApi";
import { toast } from "helpers/generalHelper";
import { normalizeTenorExpiryNewlines } from "helpers/Pricer/Table";
import {
  buildAdditionalDataOptionRows,
  buildAdditionalDataTopTable,
  getAdditionalDataFieldForMetric,
  isBlack76GridField,
} from "helpers/additionalDataTable";
import { PricerContext } from "contexts/PricerContext";
import useDraggableModal from "hooks/useDraggableModal";

const ADDITIONAL_DATA = "Additional Data";
const EXIT = "Cancel";
const SHORTHAND_KEY = "shorthand";
const EXPIRY_DATE_KEY = "expiry_date";
const BID_SIDE = "bid";
const ASK_SIDE = "ask";

export function formatTenorExpiryDisplay(value) {
  return normalizeTenorExpiryNewlines(value ?? "");
}

export function isExpiryDateField(item) {
  if (item?.key === EXPIRY_DATE_KEY) return true;
  const label = String(item?.label ?? "").toLowerCase();
  return label.includes("expiry") && label.includes("date");
}

/** Staging-style display: "87,180.00 EUR" (unit after the number, same string). */
export function formatAdditionalDataValueWithUnit(value, symbol) {
  const v = value == null || value === "" ? "" : String(value).trim();
  const u = symbol == null || symbol === "" ? "" : String(symbol).trim();
  if (!v) return "";
  if (!u) return v;
  if (v.endsWith(` ${u}`) || v.endsWith(u)) return v;
  return `${v} ${u}`;
}

function AdditionalDataMetricCell({ field, isTenor }) {
  const displayValue = isTenor
    ? formatTenorExpiryDisplay(field?.value)
    : formatAdditionalDataValueWithUnit(field?.value, field?.symbol);

  return (
    <div
      className={
        isTenor
          ? "additional-data__cell additional-data__cell--tenor"
          : "additional-data__cell additional-data__cell--metric"
      }
    >
      <Tooltip placement="top" title={displayValue || undefined} color="gray">
        {isTenor ? (
          <Input.TextArea
            disabled
            readOnly
            autoSize={{ minRows: 1, maxRows: 16 }}
            value={displayValue}
            className="additional-data__tenor-input"
          />
        ) : (
          <Input disabled value={displayValue} />
        )}
      </Tooltip>
    </div>
  );
}

function AdditionalDataSideHeader({ label, columnCount }) {
  if (columnCount <= 0) return null;
  return (
    <div
      className="additional-data__side-header"
      style={{ gridColumn: `span ${columnCount}` }}
    >
      {label}
    </div>
  );
}

function AdditionalDataOptionValueCell({
  item,
  spanCount,
  values,
  savingKeys,
  errors,
  onChange,
  onCommit,
  onApplySuggestedValue,
}) {
  if (spanCount <= 0) return null;

  if (!item) {
    return (
      <div
        className="additional-data__cell additional-data__cell--option additional-data__cell--option-empty"
        style={{ gridColumn: `span ${spanCount}` }}
      />
    );
  }

  const key = item?.key ?? null;
  const isExpiryDate = isExpiryDateField(item);
  const isEditable = Boolean(item.is_editable);
  const rawValue =
    key && Object.prototype.hasOwnProperty.call(values, key)
      ? values[key]
      : item?.value;
  // Read-only: bake unit into the string like staging ("87,180.00 EUR").
  // Editable: keep the raw value so saves don't include the unit suffix.
  const currentValue = isExpiryDate
    ? formatTenorExpiryDisplay(rawValue)
    : isEditable
      ? rawValue
      : formatAdditionalDataValueWithUnit(rawValue, item.symbol);

  const width =
    key === SHORTHAND_KEY
      ? (currentValue?.toString().length ||
          item.value?.toString().length ||
          0) *
          8 +
        70
      : isExpiryDate
        ? undefined
        : null;

  const suggestedValueDisplay =
    isEditable && isBlack76GridField(item) && item?.suggested_value != null
      ? formatConstraintValue(item.suggested_value, item.decimals)
      : null;

  return (
    <div
      className="additional-data__cell additional-data__cell--option"
      style={{ gridColumn: `span ${spanCount}` }}
    >
      {isEditable ? (
        <InputWithLabel
          label=""
          itemKey={key}
          value={currentValue}
          isDisabled={false}
          min={item.min}
          max={item.max}
          decimals={item.decimals}
          width={width}
          multiline={isExpiryDate}
          onChange={onChange}
          onCommit={onCommit}
          isSaving={key ? savingKeys.has(key) : false}
          error={key ? errors[key] : undefined}
          suggestedValueDisplay={suggestedValueDisplay}
          onApplySuggestedValue={
            suggestedValueDisplay && key
              ? () => onApplySuggestedValue(key, item.suggested_value)
              : undefined
          }
        />
      ) : (
        <Input
          disabled
          value={currentValue ?? ""}
          className={isExpiryDate ? "additional-data__tenor-input" : undefined}
        />
      )}
    </div>
  );
}

const AdditionalDataModal = ({
  isOpen = false,
  handleMenuItemClicked = () => {},
  rowSelectedInfo = "",
}) => {
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const { refetchOptionRow } = useContext(PricerContext);
  const { modalRender, title: draggableTitle } = useDraggableModal(
    isOpen,
    ADDITIONAL_DATA
  );

  // Server-driven payload pieces.
  const [measurementData, setMeasurementData] = useState([]);
  const [legs, setLegs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Per-field local edit state (keyed by item.key).
  const [values, setValues] = useState({});
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [errors, setErrors] = useState({});

  // Refs used by async save callbacks to avoid stale closures and to
  // protect in-progress edits from being overwritten by server responses.
  const valuesRef = useRef({});
  const lastCommittedRef = useRef({});
  const dirtyRef = useRef(new Set());
  const flushTimersRef = useRef(new Map());
  const closedRef = useRef(false);
  const summaryId = rowSelectedInfo?.summary_id;

  // Replace popup state from a /additional-info payload (initial GET, or
  // returned by every PUT). For editable fields we preserve any value the
  // user is currently typing — that's what kills the "revert on Enter" race.
  const applyAdditionalDataPayload = useCallback((data) => {
    const {
      legs: nextLegs,
      columns: nextColumns,
      bottom_values,
      summary: summaryData,
    } = data || {};
    setLegs(nextLegs ?? []);
    setMeasurementData(bottom_values ?? []);
    setColumns(nextColumns ?? []);
    setSummary(summaryData ?? null);

    setValues((prev) => {
      const next = { ...prev };
      (bottom_values ?? []).forEach((item) => {
        if (item?.key == null) return;
        if (dirtyRef.current.has(item.key)) return;
        next[item.key] = item.value;
        lastCommittedRef.current[item.key] = item.value;
      });
      valuesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!summaryId) return;
    setIsLoading(true);
    fetchAdditionalData(rowSelectedInfo)
      .then((res) => applyAdditionalDataPayload(res.data?.data))
      .catch((err) => console.log({ err }))
      .finally(() => setIsLoading(false));
    // We deliberately depend on summaryId only — refetching on every parent
    // re-render would re-show the loading spinner and discard local edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryId]);

  const clearTimer = useCallback((key) => {
    const timer = flushTimersRef.current.get(key);
    if (timer) {
      clearTimeout(timer);
      flushTimersRef.current.delete(key);
    }
  }, []);

  const revertFieldToLastCommitted = useCallback((key) => {
    if (!key) return;
    if (!Object.prototype.hasOwnProperty.call(lastCommittedRef.current, key)) {
      return;
    }

    const committed = lastCommittedRef.current[key];
    dirtyRef.current.delete(key);
    setValues((prev) => {
      const next = { ...prev, [key]: committed };
      valuesRef.current = next;
      return next;
    });
  }, []);

  const saveField = useCallback(
    (key) => {
      if (!summaryId || closedRef.current) return;
      if (!dirtyRef.current.has(key)) return;

      const sentValue = valuesRef.current[key];
      clearTimer(key);

      setSavingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });

      updateAdditionalInfoField(summaryId, key, sentValue)
        .then((res) => {
          if (closedRef.current) return;
          // Only mark clean if the user hasn't typed something newer in the
          // meantime — otherwise leave it dirty for a later commit.
          if (valuesRef.current[key] === sentValue) {
            dirtyRef.current.delete(key);
            lastCommittedRef.current[key] = sentValue;
          }
          applyAdditionalDataPayload(res.data?.data);
          if (res?.data?.message) toast(res.data.message, "success");
          refetchOptionRow();
        })
        .catch((err) => {
          if (closedRef.current) return;
          const errs = err?.response?.data?.errors;
          const fieldError = errs?.value?.[0];
          const generalError =
            errs?.key?.[0] ||
            errs?.calculation_model?.[0] ||
            errs?.summary_id?.[0] ||
            err?.response?.data?.message;

          revertFieldToLastCommitted(key);

          if (fieldError) {
            setErrors((prev) => ({ ...prev, [key]: fieldError }));
          } else if (generalError) {
            setErrors((prev) => {
              if (!prev[key]) return prev;
              const next = { ...prev };
              delete next[key];
              return next;
            });
            toast(generalError, "error");
          }
        })
        .finally(() => {
          if (closedRef.current) return;
          setSavingKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        });
    },
    [
      summaryId,
      applyAdditionalDataPayload,
      refetchOptionRow,
      clearTimer,
      revertFieldToLastCommitted,
    ]
  );

  const handleChange = useCallback(
    (key, value) => {
      if (!key) return;
      clearTimer(key);
      dirtyRef.current.add(key);
      setValues((prev) => {
        const next = { ...prev, [key]: value };
        valuesRef.current = next;
        return next;
      });
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [clearTimer]
  );

  // Save on blur; Enter blurs the field first (see InputWithLabel).
  const handleCommit = useCallback(
    (key) => {
      if (!key) return;
      clearTimer(key);
      if (!dirtyRef.current.has(key)) return;
      saveField(key);
    },
    [saveField, clearTimer]
  );

  const handleApplySuggestedValue = useCallback(
    (key, suggestedValue) => {
      if (!key || suggestedValue == null || suggestedValue === "") return;
      handleChange(key, suggestedValue);
      saveField(key);
    },
    [handleChange, saveField]
  );

  // Cleanup on unmount: ignore late responses; commits happen on blur/Enter only.
  useEffect(() => {
    const flushTimers = flushTimersRef.current;
    return () => {
      closedRef.current = true;
      flushTimers.forEach((timer) => clearTimeout(timer));
      flushTimers.clear();
    };
  }, []);

  const topTable = useMemo(
    () => buildAdditionalDataTopTable(columns, summary, legs),
    [columns, summary, legs]
  );

  const optionRows = useMemo(
    () => buildAdditionalDataOptionRows(measurementData),
    [measurementData]
  );

  const { metrics, bidEntities, askEntities, showBid, showAsk } = topTable;

  const bidColCount = bidEntities.length;
  const askColCount = askEntities.length;
  // Option-only payloads still need one value track for full-width spans.
  const layoutBidCols =
    bidColCount > 0 || askColCount > 0 ? bidColCount : optionRows.length ? 1 : 0;
  const layoutAskCols = askColCount;
  const dataColCount = layoutBidCols + layoutAskCols;

  const hasEntityTable =
    metrics.length > 0 && (bidColCount > 0 || askColCount > 0);
  const hasTable = hasEntityTable || optionRows.length > 0;

  const closeModal = () =>
    handleMenuItemClicked(ACTION_BUTTONS_NAME.ADDITIONAL_DATA.key, false);

  return (
    <Modal
      key="additional-data"
      className={`additional-data__container ${
        dark_Theme && "ant-modal-dark content_dark"
      }`}
      title={draggableTitle}
      modalRender={modalRender}
      open={isOpen}
      cancelText={EXIT}
      cancelButtonProps={{
        key: "back_additional_data",
        style: {
          backgroundColor: "transparent",
          color: "var(--color-red-3)",
          borderColor: "var(--color-red-3)",
        },
        onClick: closeModal,
      }}
      onCancel={closeModal}
      getContainer={".audit-trail__container"}
    >
      {isLoading ? (
        <PreLoadPagesSpin
          className={"additional-data__container__loading"}
          dark_Theme={dark_Theme}
        />
      ) : (
        <div
          className="additional-data__container__table-wrap"
          style={
            hasTable
              ? {
                  "--additional-data-bid-cols": Math.max(layoutBidCols, 0),
                  "--additional-data-ask-cols": Math.max(layoutAskCols, 0),
                }
              : undefined
          }
        >
          {hasTable && (
            <div className="additional-data__table">
              {hasEntityTable && (
                <>
                  <div className="additional-data__row additional-data__row--side-header">
                    <div className="additional-data__row-label" />
                    {showBid && (
                      <AdditionalDataSideHeader
                        label="Bid"
                        columnCount={bidColCount}
                      />
                    )}
                    {showAsk && (
                      <AdditionalDataSideHeader
                        label="Ask"
                        columnCount={askColCount}
                      />
                    )}
                  </div>

                  <div className="additional-data__row additional-data__row--header header-name">
                    <div className="additional-data__row-label" />
                    {bidEntities.map((entity) => (
                      <div
                        key={`bid-h-${entity.id}`}
                        className={`additional-data__header-cell${
                          entity.isSummary
                            ? " additional-data__header-cell--summary"
                            : ""
                        }`}
                      >
                        {entity.label}
                      </div>
                    ))}
                    {askEntities.map((entity) => (
                      <div
                        key={`ask-h-${entity.id}`}
                        className={`additional-data__header-cell${
                          entity.isSummary
                            ? " additional-data__header-cell--summary"
                            : ""
                        }`}
                      >
                        {entity.label}
                      </div>
                    ))}
                  </div>

                  {metrics.map((metric) => (
                    <div
                      key={metric.id}
                      className="additional-data__row cell-value"
                    >
                      <div className="additional-data__row-label">
                        {metric.label}
                      </div>
                      {bidEntities.map((entity) => (
                        <AdditionalDataMetricCell
                          key={`bid-${metric.id}-${entity.id}`}
                          field={getAdditionalDataFieldForMetric(
                            entity.fields,
                            metric,
                            BID_SIDE
                          )}
                          isTenor={metric.isTenor}
                        />
                      ))}
                      {askEntities.map((entity) => (
                        <AdditionalDataMetricCell
                          key={`ask-${metric.id}-${entity.id}`}
                          field={getAdditionalDataFieldForMetric(
                            entity.fields,
                            metric,
                            ASK_SIDE
                          )}
                          isTenor={metric.isTenor}
                        />
                      ))}
                    </div>
                  ))}
                </>
              )}

              {optionRows.length > 0 && (
                <>
                  {hasEntityTable && (
                    <div className="additional-data__row additional-data__row--section-divider">
                      <div
                        className="additional-data__section-divider"
                        style={{ gridColumn: `1 / span ${dataColCount + 1}` }}
                      />
                    </div>
                  )}

                  {optionRows.map((row) => (
                    <div
                      key={row.id}
                      className="additional-data__row additional-data__row--option cell-value"
                    >
                      <div className="additional-data__row-label">
                        {row.label}
                      </div>
                      {row.span === "all" ? (
                        <AdditionalDataOptionValueCell
                          item={row.shared}
                          spanCount={dataColCount}
                          values={values}
                          savingKeys={savingKeys}
                          errors={errors}
                          onChange={handleChange}
                          onCommit={handleCommit}
                          onApplySuggestedValue={handleApplySuggestedValue}
                        />
                      ) : (
                        <>
                          {layoutBidCols > 0 && (
                            <AdditionalDataOptionValueCell
                              item={row.bid}
                              spanCount={layoutBidCols}
                              values={values}
                              savingKeys={savingKeys}
                              errors={errors}
                              onChange={handleChange}
                              onCommit={handleCommit}
                              onApplySuggestedValue={handleApplySuggestedValue}
                            />
                          )}
                          {layoutAskCols > 0 && (
                            <AdditionalDataOptionValueCell
                              item={row.ask}
                              spanCount={layoutAskCols}
                              values={values}
                              savingKeys={savingKeys}
                              errors={errors}
                              onChange={handleChange}
                              onCommit={handleCommit}
                              onApplySuggestedValue={handleApplySuggestedValue}
                            />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AdditionalDataModal;
